import { useState, useEffect } from 'react'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Sector,
  SectorProps,
} from 'recharts'
import { useAppSelector } from '@/hooks/storeHooks/storehooks'
import GradiantSkelton from '@/components/atom/gradiantSkelton/gradiantSkelton'
import { DropDownCmp } from '@/components/atom/dropdown/dropDown'
import { fetchOverViewSiteMetrics } from '@/services/apis/api'
import { formatDefPayload, getFromStorage, saveToStorage } from '@/lib/utils'
import { toast } from 'sonner'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable'
import SortableTabCard from './sortableTabCard'
import { useNavigate } from 'react-router-dom'

const renderActiveShape = (props: SectorProps & { midAngle?: number }) => {
  const {
    cx,
    cy,
    innerRadius = 0,
    outerRadius = 0,
    startAngle,
    endAngle,
    fill,
    midAngle = 0,
  } = props

  const RADIAN = Math.PI / 180
  const translate = 6
  const cxVal = cx ?? 0
  const cyVal = cy ?? 0

  const x = cxVal + translate * Math.cos(-midAngle * RADIAN)
  const y = cyVal + translate * Math.sin(-midAngle * RADIAN)

  return (
    <Sector
      cx={x}
      cy={y}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 3}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
      style={{
        cursor: 'pointer',
      }}
    />
  )
}

const SITES_TAB_CARD_IDS = ['site-status', 'site-status-metrics']

// ─── Component ────────────────────────────────────────────────────────────────

const SitesTab = ({
  isWidgetVisible,
}: {
  isWidgetVisible: (key: string) => boolean
}) => {
  const navigate = useNavigate()
  const [metricsFilter, setMetricsFilter] = useState('CURRENT_WEEK')
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [siteCards, setSiteCards] = useState(() => {
    const saved = getFromStorage('service_siteCards')
    return Array.isArray(saved) ? saved : SITES_TAB_CARD_IDS
  })

  useEffect(() => {
    saveToStorage('service_siteCards', siteCards)
  }, [siteCards])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  )

  const defPayload = useAppSelector(state => state.menu.payloadDef)
  const [filteredMetrics, setFilteredMetrics] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    overdue: 0,
  })
  const [isFilteredLoading, setIsFilteredLoading] = useState(true)

  useEffect(() => {
    const fetchMetrics = async () => {
      setIsFilteredLoading(true)
      try {
        const res = (await fetchOverViewSiteMetrics({
          datetime: metricsFilter,
          ...formatDefPayload(defPayload),
        })) as {
          status: number
          data: {
            data: {
              metrics: {
                total: number
                completed: number
                inProgress: number
                overdue: number
              }
            }
          }
        }
        if (res.status === 200) {
          setFilteredMetrics(res.data.data.metrics)
        } else {
          toast.error('Failed to load metrics')
        }
      } catch {
        toast.error('Failed to load metrics')
      } finally {
        setIsFilteredLoading(false)
      }
    }

    if (defPayload.siteId.length > 0 && defPayload.customerId) {
      fetchMetrics()
    }
  }, [metricsFilter, defPayload])

  const _rawSiteMetrics = useAppSelector(
    state => state.sitesOverview.overViewSiteMetrics,
  ) as unknown

  const siteMetricsData = (() => {
    const src = (_rawSiteMetrics || {}) as Record<string, unknown>
    return {
      total: Number(src.total || 0),
      completed: Number(src.completed || 0),
      inProgress: Number(src.inProgress || 0),
      overdue: Number(src.overdue ?? src.pastDue ?? 0),
    }
  })()

  const isSitesLoading = useAppSelector(
    state => state.sitesOverview.isSiteMetricsLoading,
  )

  const donutData = [
    {
      name: 'Completed',
      value: siteMetricsData?.completed || 0,
      color: '#1E8E5A',
      route: '/service-delivery/sites',
    },
    {
      name: 'In Progress',
      value: siteMetricsData?.inProgress || 0,
      color: '#D97706',
      route: '/service-delivery/sites',
    },
    {
      name: 'Overdue',
      value: siteMetricsData?.overdue || 0,
      color: '#C62828',
      // route: '/sites/overview?status=OVERDUE',
      route: '/service-delivery/sites',
    },
  ]

  const total = siteMetricsData?.total || 0

  const filteredTotal = filteredMetrics?.total || 0
  const filteredCompletedPct =
    filteredTotal > 0
      ? ((filteredMetrics.completed || 0) / filteredTotal) * 100
      : 0
  const filteredInProgressPct =
    filteredTotal > 0
      ? ((filteredMetrics.inProgress || 0) / filteredTotal) * 100
      : 0
  const filteredOverduePct =
    filteredTotal > 0
      ? ((filteredMetrics.overdue || 0) / filteredTotal) * 100
      : 0

  const handleSitesDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setSiteCards(items => {
        const oldIndex = items.indexOf(active.id as string)
        const newIndex = items.indexOf(over.id as string)

        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const handleDrilldown = (item: (typeof donutData)[number]) => {
    navigate(item.route, { state: { item } })
  }

  const renderSiteCard = (cardId: string) => {
    switch (cardId) {
      case 'site-status':
        if (!isWidgetVisible('site_status')) return null
        return (
          <SortableTabCard key={cardId} id={cardId} className='h-full'>
            <div className='flex h-full w-full flex-col rounded-xl border border-border border-l-[4px] border-l-accent bg-primary p-4 shadow-sm'>
              <div className='mb-4 flex items-center justify-between'>
                <h3 className='text-kpi-heading-2 font-semibold text-primary-foreground'>
                  Site Status
                </h3>
                <div className='flex items-center gap-2'></div>
              </div>
              {isSitesLoading ? (
                <div className='relative flex h-56 items-center justify-center'>
                  {/* Donut Skeleton */}
                  <div className='relative flex items-center justify-center'>
                    <GradiantSkelton
                      width='w-[190px]'
                      height='h-[190px]'
                      className='rounded-full'
                    />
                    <div className='absolute h-[120px] w-[120px] rounded-full bg-primary' />
                  </div>

                  <div className='absolute inset-0 flex flex-col items-center justify-center'>
                    <GradiantSkelton
                      width='w-16'
                      height='h-8'
                      className='rounded-md'
                    />
                    <GradiantSkelton
                      width='w-12'
                      height='h-3'
                      className='mt-2 rounded-full'
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className='relative flex h-56 items-center justify-center'>
                    <ResponsiveContainer width='100%' height='100%'>
                      <PieChart>
                        <Pie
                          data={donutData}
                          dataKey='value'
                          innerRadius={60}
                          outerRadius={95}
                          paddingAngle={2}
                          activeIndex={activeIndex ?? undefined}
                          activeShape={renderActiveShape}
                          onMouseEnter={(_, index) => setActiveIndex(index)}
                          onMouseLeave={() => setActiveIndex(null)}
                          animationDuration={250}
                          onClick={(_, index) =>
                            handleDrilldown(donutData[index])
                          }
                        >
                          {donutData.map((entry, index) => (
                            <Cell
                              key={index}
                              fill={entry.color}
                              style={{
                                cursor: 'pointer',
                                transition: 'opacity 0.3s ease',
                              }}
                              opacity={
                                activeIndex === null || activeIndex === index
                                  ? 1
                                  : 0.35
                              }
                            />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>

                    <div className='pointer-events-none absolute inset-0 flex flex-col items-center justify-center'>
                      <span className='text-kpi-secondary-value font-semibold'>
                        {activeIndex !== null
                          ? donutData[activeIndex].value
                          : total}
                      </span>
                      <span className='mt-1 text-badge font-semibold text-primary-foreground'>
                        {activeIndex !== null
                          ? donutData[activeIndex].name
                          : 'Total'}
                      </span>
                    </div>
                  </div>
                  <div className='mt-2 flex items-center justify-center gap-6'>
                    {donutData.map((item, i) => (
                      <div
                        key={i}
                        className='flex items-center gap-2 text-kpi-supporting-1 primary-foreground'
                      >
                        <div
                          className='h-3 w-3 rounded-sm'
                          style={{ backgroundColor: item.color }}
                        />
                        <span>
                          {item.name} ({item.value})
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </SortableTabCard>
        )
      case 'site-status-metrics':
        if (!isWidgetVisible('site_status_metrics')) return null
        return (
          <SortableTabCard
            key={cardId}
            id={cardId}
            className='h-full md:col-span-2'
          >
            <div className='flex h-full w-full flex-col rounded-xl border border-border border-l-[4px] border-l-accent bg-primary p-4 shadow-sm'>
              <div className='mb-4 flex items-center justify-between'>
                <h3 className='text-kpi-heading-2 font-semibold text-primary-foreground'>
                  Site Status Metrics
                </h3>
                <div className='flex items-center gap-2'>
                  <DropDownCmp
                    node={{
                      fieldName: 'siteStatusMetricsDropdown',
                      dropdownValue: [
                        { label: 'Last Month', value: 'LAST_MONTH' },
                        { label: 'Last Week', value: 'LAST_WEEK' },
                        { label: 'Last 30 days', value: 'LAST_30_DAYS' },
                        { label: 'Last 7 days', value: 'LAST_7_DAYS' },
                        { label: 'Current Week', value: 'CURRENT_WEEK' },
                      ],
                      value: metricsFilter,
                      childClass:
                        'h-8 text-kpi-supporting-1 font-medium bg-tertiary border-none rounded-full px-4',
                    }}
                    handleChange={(_, val) => setMetricsFilter(val)}
                  />
                </div>
              </div>

              {isFilteredLoading ? (
                <>
                  {/* Progress Bar Skeleton */}
                  <div className='flex flex-1 items-center'>
                    <div className='flex w-full items-center'>
                      <div className='flex h-[129px] w-full overflow-hidden rounded-md bg-gray-100 dark:bg-secondary/40'>
                        <GradiantSkelton
                          width='w-1/3'
                          height='h-full'
                          className='!rounded-none border-r-4 border-white dark:border-primary'
                        />

                        <GradiantSkelton
                          width='w-1/3'
                          height='h-full'
                          className='!rounded-none border-r-4 border-white dark:border-primary'
                        />

                        <GradiantSkelton
                          width='w-1/3'
                          height='h-full'
                          className='!rounded-none'
                        />
                      </div>
                    </div>
                  </div>

                  {/* Legend Skeleton */}
                  <div className='mt-3 flex items-center justify-center gap-6'>
                    {[1, 2, 3].map(item => (
                      <div key={item} className='flex items-center gap-2'>
                        <GradiantSkelton
                          width='w-3'
                          height='h-3'
                          className='!rounded-sm'
                        />

                        <GradiantSkelton
                          width='w-20'
                          height='h-4'
                          className='!rounded-md'
                        />
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className='flex flex-1 items-center '>
                    <div className='flex w-full items-center'>
                      <div className='flex h-[129px] w-full overflow-hidden rounded-md bg-gray-100'>
                        {filteredTotal === 0 ? (
                          <>
                            <div
                              className='flex items-center justify-center bg-success text-primary-foreground-invert font-semibold text-kpi-primary-value border-r-4 border-primary'
                              style={{ width: '33.33%' }}
                            >
                              0
                            </div>
                            <div
                              className='flex items-center justify-center bg-warning text-primary-foreground-invert font-semibold text-kpi-primary-value border-r-4 border-primary'
                              style={{ width: '33.33%' }}
                            >
                              0
                            </div>
                            <div
                              className='flex items-center justify-center bg-danger text-primary-foreground-invert font-semibold text-kpi-primary-value'
                              style={{ width: '33.33%' }}
                            >
                              0
                            </div>
                          </>
                        ) : (
                          <>
                            {filteredCompletedPct > 0 && (
                              <div
                                className={`flex items-center justify-center bg-[#1E8E5A] text-white font-semibold text-page-title ${filteredInProgressPct > 0 || filteredOverduePct > 0 ? 'border-r-4 border-white' : ''}`}
                                style={{ width: `${filteredCompletedPct}%` }}
                              >
                                {filteredMetrics.completed}
                              </div>
                            )}
                            {filteredInProgressPct > 0 && (
                              <div
                                className={`flex items-center justify-center bg-[#D97706] text-white font-semibold text-page-title ${filteredOverduePct > 0 ? 'border-r-4 border-white' : ''}`}
                                style={{ width: `${filteredInProgressPct}%` }}
                              >
                                {filteredMetrics.inProgress}
                              </div>
                            )}
                            {filteredOverduePct > 0 && (
                              <div
                                className='flex items-center justify-center bg-[#C62828] text-white font-semibold text-page-title'
                                style={{ width: `${filteredOverduePct}%` }}
                              >
                                {filteredMetrics.overdue}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='mt-2 mb-2 flex items-center justify-center gap-6'>
                    <div className='flex items-center gap-2 text-kpi-supporting-1 text-primary-foreground'>
                      <div className='h-3 w-3 rounded-sm bg-success' />
                      <span>Completed</span>
                    </div>
                    <div className='flex items-center gap-2 text-kpi-supporting-1 text-primary-foreground'>
                      <div className='h-3 w-3 rounded-sm bg-warning' />
                      <span>In Progress</span>
                    </div>
                    <div className='flex items-center gap-2 text-kpi-supporting-1 text-primary-foreground'>
                      <div className='h-3 w-3 rounded-sm bg-danger' />
                      <span>Overdue</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </SortableTabCard>
        )
      default:
        return null
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleSitesDragEnd}
    >
      <SortableContext items={siteCards} strategy={rectSortingStrategy}>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
          {siteCards.map(renderSiteCard)}
        </div>
      </SortableContext>
    </DndContext>
  )
}

export default SitesTab
